import React, { useState } from 'react';

import { Alert, Button, Stack, Typography } from '@mui/material';

import {
  PromoWarningResolutionKey,
  VoucherWarningResolutionKey,
  warningResolutionConfig,
} from '@luxuryescapes/contract-svc-promo';

import type { PromoResolutionTarget, PromoWarningResolution } from './PromoResolution';

interface Props {
  resolution: PromoWarningResolution;
  target?: PromoResolutionTarget;
}

/** Presents contract guidance with an optional caller-provided repair or navigation action. */
export default function ResolutionGuidance({ resolution, target }: Props) {
  const [isResolving, setIsResolving] = useState(false);
  const [error, setError] = useState<string>();

  const { key, msg } = resolution;
  let message = msg;
  let isKnownKey = false;

  switch (key) {
    case PromoWarningResolutionKey.SET_EXPIRY_DATE:
    case PromoWarningResolutionKey.DISCOUNT_SET_PERCENTAGE:
    case PromoWarningResolutionKey.DISCOUNT_SET_MAX:
    case PromoWarningResolutionKey.DISCOUNT_SET_AMOUNT:
    case PromoWarningResolutionKey.ENABLE_ZERO_VALUE_TOGGLE:
    case PromoWarningResolutionKey.SET_LOYALTY_APPLY_METHOD:
    case PromoWarningResolutionKey.SET_EMAIL_DOMAINS:
    case PromoWarningResolutionKey.ADD_DISCOUNT:
    case PromoWarningResolutionKey.DISCOUNT_SET_RESTRICTIONS:
    case PromoWarningResolutionKey.DISCOUNT_SET_PRODUCTS:
    case PromoWarningResolutionKey.SET_CATEGORIES:
    case PromoWarningResolutionKey.SET_ACTIVE_TAGS:
    case PromoWarningResolutionKey.SET_AIRLINE_CARRIERS:
    case PromoWarningResolutionKey.SET_TYPE:
    case PromoWarningResolutionKey.DISCOUNT_ALLOW_FIRST_USE:
    case PromoWarningResolutionKey.CONDITION_SET_DISCOUNT_IDS:
    case PromoWarningResolutionKey.CONDITION_REMOVE_DISCOUNT_IDS:
    case PromoWarningResolutionKey.ADD_REQUIRED_TOGGLE:
    case VoucherWarningResolutionKey.SET_BRAND:
    case VoucherWarningResolutionKey.ADD_REWARD:
    case VoucherWarningResolutionKey.REWARD_SET_CURRENCY:
    case VoucherWarningResolutionKey.REWARD_SET_AMOUNT:
    case VoucherWarningResolutionKey.REWARD_SET_MIN_SPEND:
    case VoucherWarningResolutionKey.SET_EXPIRY_DATE_OR_SHOW_VOUCHER_UNTIL_DATE:
    case VoucherWarningResolutionKey.SET_EXPIRY_DATE:
    case VoucherWarningResolutionKey.SET_LIABILITY:
    case VoucherWarningResolutionKey.CONDITION_SET_REWARD_IDS:
    case VoucherWarningResolutionKey.SET_PRODUCT_TYPE:
    case VoucherWarningResolutionKey.UPDATE_TEXT_REPLACEMENTS:
    case VoucherWarningResolutionKey.DETAILS_USE_CURRENCY_REPLACEMENT:
    case VoucherWarningResolutionKey.UPDATE_DISPLAY_CONTEXT:
    case VoucherWarningResolutionKey.SET_SUBSCRIPTION_REQUIREMENT:
    case VoucherWarningResolutionKey.REWARD_SET_PARTIAL_REDEMPTION_AMOUNTS:
    case VoucherWarningResolutionKey.ADD_REQUIRED_TOGGLE:
    case VoucherWarningResolutionKey.RETRY_TRANSLATION:
    case VoucherWarningResolutionKey.RETRY_DISPLAY_CONFIG_TRANSLATION:
      isKnownKey = true;
      message ||= [...warningResolutionConfig.promo, ...warningResolutionConfig.voucher].find(
        (entry) => entry.key === key,
      )?.explanation;
      break;
    default:
      break;
  }

  async function handleResolve(currentResolution: PromoWarningResolution) {
    if (!target || !('onResolve' in target) || isResolving) return;

    setIsResolving(true);
    setError(undefined);
    try {
      await target.onResolve(currentResolution);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Unable to resolve this warning. Please try again.');
    } finally {
      setIsResolving(false);
    }
  }

  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
      {!isKnownKey && <Typography variant="body2">{key}</Typography>}
      <Typography variant="body2">{message}</Typography>
      {isKnownKey &&
        target &&
        ('href' in target ? (
          <Button href={target.href} color="primary" variant="text" size="small">
            {target.label}
          </Button>
        ) : (
          <Button
            onClick={() => handleResolve(resolution)}
            disabled={isResolving || target.disabled}
            color="primary"
            variant="text"
            size="small"
          >
            {isResolving ? 'Resolving…' : target.label}
          </Button>
        ))}
      {error && <Alert severity="error">{error}</Alert>}
    </Stack>
  );
}
